import { useFormContext } from 'react-hook-form';
import { realisation as rules } from 'further-ui/validations';
import { AllocationRule } from 'further-types/lp/allocations';
import { realisations } from 'further-ui/domain';
const { calculateNewInvestorAllocation } = realisations;

const useSyncInvestorAllocations = () => {
  const { getValues, setValue } =
    useFormContext<rules.CreateRealisationFormValues>();

  const setInvestorAllocation = ({
    considerationIndex,
    allocationIndex,
    newAllocationPercentage,
  }: {
    considerationIndex: number;
    allocationIndex: number;
    newAllocationPercentage: number;
  }) => {
    const holdingClassRealisations = getValues('holdingClassRealisation');
    const allInvestorAllocations = getValues(
      `consideration.${considerationIndex}.investorAllocations`,
    );

    const investorAllocation = getValues(
      `consideration.${considerationIndex}.investorAllocations.${allocationIndex}`,
    );
    const withholdingTaxList = getValues('investorWithholdingTaxList');

    const newConsiderationValues = calculateNewInvestorAllocation({
      holdingClassId: investorAllocation.holdingClassId,
      investmentId: investorAllocation.investmentId,
      commitmentId: investorAllocation.commitmentId,
      newAllocationPercentage,
      holdingClassRealisations,
      withholdingTaxList: withholdingTaxList ?? [],
      investorAllocations: allInvestorAllocations ?? [],
    });

    setValue(
      `consideration.${considerationIndex}.investorAllocations.${allocationIndex}.unitsAllocated`,
      newConsiderationValues.newUnitsAllocated,
    );

    setValue(
      `consideration.${considerationIndex}.investorAllocations.${allocationIndex}.receivedConsideration`,
      newConsiderationValues.newConsiderationValue,
    );

    setValue(
      `consideration.${considerationIndex}.investorAllocations.${allocationIndex}.escrowConsideration`,
      newConsiderationValues.newEscrowConsiderationValue,
    );

    setValue(
      `consideration.${considerationIndex}.investorAllocations.${allocationIndex}.earnoutConsideration`,
      newConsiderationValues.newEarnoutConsiderationValue,
    );

    setValue(
      `consideration.${considerationIndex}.investorAllocations.${allocationIndex}.withholdingTaxPayable`,
      newConsiderationValues.newWithholdingTaxPayable,
    );
  };

  const setInvestorAllocations = (considerationIndex: number) => {
    const investorAllocations =
      getValues(`consideration.${considerationIndex}.investorAllocations`) ??
      [];

    for (const investorAllocation of investorAllocations) {
      const allocationIndex = investorAllocations.findIndex(
        (investment) =>
          investment.investmentId === investorAllocation.investmentId &&
          investment.holdingClassId === investorAllocation.holdingClassId &&
          investment.commitmentId === investorAllocation.commitmentId,
      );

      const newAllocationPercentage = getValues(
        `consideration.${considerationIndex}.investorAllocations.${allocationIndex}.allocationPercentage`,
      );

      setInvestorAllocation({
        considerationIndex,
        allocationIndex,
        newAllocationPercentage,
      });
    }
  };

  const setInvestorAllocationType = async ({
    considerationIndex,
    investmentId,
    newAllocationType,
  }: {
    considerationIndex: number;
    investmentId: string;
    newAllocationType: AllocationRule;
  }) => {
    const investorAllocations =
      getValues(`consideration.${considerationIndex}.investorAllocations`) ??
      [];

    const investmentAllocationsForInvestment = investorAllocations.filter(
      (investment) => investment.investmentId === investmentId,
    );

    if (!investmentAllocationsForInvestment.length) {
      return;
    }

    for (const {
      holdingClassId,
      commitmentId,
    } of investmentAllocationsForInvestment) {
      const allocationIndex = investorAllocations.findIndex(
        (investment) =>
          investmentId === investment.investmentId &&
          investment.holdingClassId === holdingClassId &&
          commitmentId === investment.commitmentId,
      );

      if (allocationIndex === -1) {
        continue;
      }

      if (newAllocationType === AllocationRule.PerOriginalInvestment) {
        resetInvestmentAllocations({
          considerationIndex,
          allocationIndex,
        });
      }

      setInvestorAllocations(considerationIndex);

      setValue(
        `consideration.${considerationIndex}.investorAllocations.${allocationIndex}.allocationType`,
        newAllocationType,
      );
    }
  };

  const resetInvestmentAllocations = ({
    considerationIndex,
    allocationIndex,
  }: {
    considerationIndex: number;
    allocationIndex: number;
  }) => {
    const originalInvestmentAllocation = getValues(
      `consideration.${considerationIndex}.investorAllocations.${allocationIndex}.originalAllocationPercentage`,
    );

    setValue(
      `consideration.${considerationIndex}.investorAllocations.${allocationIndex}.allocationPercentage`,
      originalInvestmentAllocation,
    );
  };

  return {
    setInvestorAllocation,
    setInvestorAllocationType,
    setInvestorAllocations,
  };
};

export default useSyncInvestorAllocations;
